// Auth session (bearer token kept in localStorage of this browser only) and the data-fetching hook.

import { ApiError, GrowthApi, type Me } from '@growth/client';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const TOKEN_KEY = 'growth.token';

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // storage unavailable (private mode): the session lasts until reload
  }
}

interface SessionValue {
  api: GrowthApi;
  me: Me | null;
  isAdmin: boolean;
  status: 'checking' | 'anonymous' | 'ready';
  login: (token: string) => Promise<void>;
  logout: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children, api: injected }: { children: ReactNode; api?: GrowthApi }) {
  const api = useMemo(() => injected ?? new GrowthApi({ baseUrl: '', token: readToken() }), [injected]);
  const [me, setMe] = useState<Me | null>(null);
  const [status, setStatus] = useState<SessionValue['status']>(api.hasToken ? 'checking' : 'anonymous');

  useEffect(() => {
    if (!api.hasToken) return;
    let alive = true;
    api.me().then(
      (m) => {
        if (!alive) return;
        setMe(m);
        setStatus('ready');
      },
      (err: unknown) => {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 401) {
          api.setToken(null);
          writeToken(null);
          setStatus('anonymous');
        } else {
          // API unreachable: keep the token, show pages with their own errors.
          setStatus('ready');
        }
      },
    );
    return () => {
      alive = false;
    };
  }, [api]);

  const login = useCallback(
    async (token: string) => {
      const probe = new GrowthApi({ baseUrl: api.baseUrl, token });
      const m = await probe.me();
      api.setToken(token);
      writeToken(token);
      setMe(m);
      setStatus('ready');
    },
    [api],
  );

  const logout = useCallback(() => {
    api.setToken(null);
    writeToken(null);
    cache.clear();
    setMe(null);
    setStatus('anonymous');
  }, [api]);

  const value = useMemo<SessionValue>(
    () => ({ api, me, isAdmin: !!me?.scopes.includes('admin'), status, login, logout }),
    [api, me, status, login, logout],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}

// ------------------------------------------------------------------ data fetching

const cache = new Map<string, unknown>();

export interface ApiState<T> {
  data: T | undefined;
  error: Error | undefined;
  /** True while a request is in flight (data may be the previous result: render it dimmed, never blank). */
  loading: boolean;
  reload: () => void;
}

/**
 * Fetches `load` whenever `key` changes (null = skip). Keeps the previous data while refetching so charts hold
 * their frame, and caches by key so navigating back is instant. `refreshMs` polls in the background.
 */
export function useApi<T>(key: string | null, load: (api: GrowthApi, signal: AbortSignal) => Promise<T>, opts: { refreshMs?: number } = {}): ApiState<T> {
  const { api, logout } = useSession();
  const loadRef = useRef(load);
  loadRef.current = load;
  const [nonce, setNonce] = useState(0);
  const [state, setState] = useState<{ key: string | null; data: T | undefined; error: Error | undefined; loading: boolean }>(() => ({
    key,
    data: key ? (cache.get(key) as T | undefined) : undefined,
    error: undefined,
    loading: !!key,
  }));

  useEffect(() => {
    if (!key) {
      setState({ key, data: undefined, error: undefined, loading: false });
      return;
    }
    const ctrl = new AbortController();
    setState((s) => ({ key, data: (cache.get(key) as T | undefined) ?? s.data, error: undefined, loading: true }));
    loadRef.current(api, ctrl.signal).then(
      (data) => {
        cache.set(key, data);
        if (cache.size > 200) cache.delete(cache.keys().next().value as string);
        if (!ctrl.signal.aborted) setState({ key, data, error: undefined, loading: false });
      },
      (error: unknown) => {
        if (ctrl.signal.aborted) return;
        if (error instanceof ApiError && error.status === 401) logout();
        setState((s) => ({ ...s, error: error as Error, loading: false }));
      },
    );
    return () => ctrl.abort();
  }, [key, nonce, api, logout]);

  useEffect(() => {
    if (!key || !opts.refreshMs) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') setNonce((n) => n + 1);
    }, opts.refreshMs);
    return () => clearInterval(id);
  }, [key, opts.refreshMs]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data: state.data, error: state.error, loading: state.loading, reload };
}

export function invalidateCache(prefix?: string): void {
  if (!prefix) return cache.clear();
  for (const k of [...cache.keys()]) if (k.startsWith(prefix)) cache.delete(k);
}

/** Runs a mutation, reporting its error; returns undefined on failure. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError((e as Error).message);
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, error, run, setError };
}
